
export type SymptomQuestion = { key: string; label: string };
export type CheckboxSection = { title: string; questions: SymptomQuestion[] };
export type AdditionalField = { key: string; label: string };

export type SymptomSectionConfig = {
    title: string;
    notes: boolean;
    questions: SymptomQuestion[];
    gender?: 'female' | 'male';
    checkboxes?: CheckboxSection;
    autoimmune?: CheckboxSection;
    additionalFields?: AdditionalField[];
};

export const symptomConfig: { [key: string]: SymptomSectionConfig } = {
    adrenalHealth: {
        title: 'Section 1: Adrenal Health',
        notes: true,
        questions: [
            { key: 'fatigueAfternoon', label: 'Fatigue in the afternoon' },
            { key: 'troubleStayingAsleep', label: 'Trouble staying asleep' },
            { key: 'cravingsSalty', label: 'Cravings for salty food' },
            { key: 'dizzinessStanding', label: 'Dizziness when standing quickly' },
            { key: 'slowStartMornings', label: 'Slow start in mornings' },
            { key: 'eyesSensitiveBright', label: 'Eyes sensitive to bright light' },
            { key: 'grindingTeeth', label: 'Grinding teeth' },
            { key: 'difficultyFallingAsleep', label: 'Difficulty falling asleep' },
            { key: 'hives', label: 'Hives' },
            { key: 'perspireNoActivity', label: 'Perspire often with no activity' },
            { key: 'wakeTired', label: 'Wake up tired even after enough sleep' },
            { key: 'weakNails', label: 'Weak nails' },
            { key: 'afternoonHeadaches', label: 'Afternoon headaches' },
            { key: 'troubleRecoveringStress', label: 'Trouble recovering from stress' },
            { key: 'headacheExertion', label: 'Headache with exertion or exercise' },
            { key: 'underHighStress', label: 'Under a high amount of stress' },
        ],
    },
    vitaminMineral: {
        title: 'Section 2: Vitamin/Mineral',
        notes: true,
        questions: [
            { key: 'lossMuscleTone', label: 'Loss of muscle tone' },
            { key: 'smallBumpsArms', label: 'Small bumps on back of arms' },
            { key: 'racingHeart', label: 'Racing heart' },
            { key: 'numbnessTingling', label: 'Numbness or tingling' },
            { key: 'feelingDepressed', label: 'Feeling depressed' },
            { key: 'gumsBleedEasily', label: 'Gums bleed easily' },
            { key: 'bodyJerksFallingAsleep', label: 'Body jerks when falling asleep' },
            { key: 'persistentWorryAnxiety', label: 'Persistent worry or anxiety' },
            { key: 'bruiseEasily', label: 'Bruise easily' },
            { key: 'nosebleeds', label: 'Nosebleeds' },
        ]
    },
    colonHealth: {
        title: 'Section 3: Colon Health',
        notes: true,
        questions: [
            { key: 'bowelsNotEmtpy', label: 'Feeling bowels do not empty completely' },
            { key: 'diarrhea', label: 'Diarrhea' },
            { key: 'ibsColitis', label: 'IBS or colitis' },
            { key: 'historyParasites', label: 'History of parasites' },
            { key: 'yeastInfections', label: 'Yeast infections' },
            { key: 'itchyAnus', label: 'Itchy anus' },
            { key: 'itchySkin', label: 'Itchy skin' },
            { key: 'nailFungus', label: 'Nail fungus' },
            { key: 'fuzzyTongue', label: '"Fuzzy” tongue' },
            { key: 'looseStools', label: 'Loose stools' },
            { key: 'lackDailyMovements', label: 'Lack of daily bowel movements' },
            { key: 'abdominalCramping', label: 'Abdominal cramping' },
            { key: 'useLaxatives', label: 'Use laxatives' },
            { key: 'passBadSmellingGas', label: 'Pass bad smelling gas' },
            { key: 'abdominalPainRelieved', label: 'Abdominal pain relieved by passing gas or having a bowel movement' },
            { key: 'bloodInStool', label: 'Blood in stool' },
            { key: 'moreThan3Movements', label: 'More than 3 bowel movements per day' },
            { key: 'alternatingDiarrheaConstipation', label: 'Alternating diarrhea and constipation' },
            { key: 'hemorrhoids', label: 'Hemorrhoids' },
            { key: 'celiacDisease', label: 'Celiac disease' },
            { key: 'bloatingGrains', label: 'Bloating after consuming grains' },
            { key: 'belching', label: 'Belching' },
            { key: 'heartburn', label: 'Heartburn' },
            { key: 'nauseaVomiting', label: 'Nausea or Vomiting' },
            { key: 'diverticulitis', label: 'Diverticulitis' },
        ]
    },
    thyroidHealth: {
        title: 'Section 4: Thyroid Health',
        notes: true,
        questions: [
          { key: 'nightSweats', label: 'Night sweats' },
          { key: 'flushEasily', label: 'Flush easily' },
          { key: 'difficultyGainingWeight', label: 'Difficulty gaining weight' },
          { key: 'easilyFatigued', label: 'Easily fatigued' },
          { key: 'coldHandsFeet', label: 'Cold hands and feet' },
          { key: 'lowBodyTemp', label: 'Low body temperature' },
          { key: 'sensitivityCold', label: 'Sensitivity to cold' },
          { key: 'intolerantHeat', label: 'Intolerant to heat' },
          { key: 'heartPalpitations', label: 'Heart palpitations' },
          { key: 'insomnia', label: 'Insomnia' },
          { key: 'fastRestingPulse', label: 'Fast resting pulse' },
          { key: 'thinningHair', label: 'Thinning hair or hair loss' },
          { key: 'thinningEyebrows', label: 'Thinning of lateral 1/3 of eyebrows' },
          { key: 'morningHeadaches', label: 'Morning headaches that go away' },
          { key: 'stubbornWeightGain', label: 'Stubborn easy weight gain' },
          { key: 'lackMotivation', label: 'Lack of motivation or depression' },
          { key: 'nervousness', label: 'Nervousness' },
          { key: 'excessivelySleeping', label: 'Excessively sleeping' },
          { key: 'drySkin', label: 'Dry skin' },
          { key: 'puffyFace', label: 'Puffy face' },
          { key: 'weaknessAches', label: 'Weakness and aches in muscles and joints' },
        ]
    },
    respiratoryHealth: {
        title: 'Section 5: Respiratory Health',
        notes: true,
        questions: [
            { key: 'asthma', label: 'Asthma' },
            { key: 'chronicSinusitis', label: 'Chronic sinusitis' },
            { key: 'exerciseInducedAsthma', label: 'Exercise induced asthma' },
            { key: 'sleepApnea', label: 'Sleep apnea' },
            { key: 'pneumonia', label: 'Pneumonia' },
            { key: 'emphysema', label: 'Emphysema' },
            { key: 'bronchitis', label: 'Bronchitis' },
        ]
    },
    liverGallbladder: {
        title: 'Section 6: Liver & Gallbladder Health',
        notes: true,
        questions: [
            { key: 'insomnia', label: 'Insomnia' },
            { key: 'stomachUpsetGreasy', label: 'Stomach gets upset after eating greasy or high-fat foods' },
            { key: 'metallicTaste', label: 'Metallic taste in mouth in the morning' },
            { key: 'eyesYellow', label: 'Eyes are yellow' },
            { key: 'excessiveHairLoss', label: 'Excessive hair loss' },
            { key: 'sensitivityPerfume', label: 'Sensitivity to perfume' },
            { key: 'sensitivityChemicals', label: 'Sensitivity to chemicals' },
            { key: 'easilyIntoxicated', label: 'Easily intoxicated after a small amount of alcohol' },
            { key: 'painRibCage', label: 'Pain under rib cage on right side' },
            { key: 'fishTastingBurps', label: 'Fish-tasting burps after taking fish oil' },
            { key: 'palmsRed', label: 'Palms of hands look red' },
            { key: 'gallstonesAttack', label: 'Gallstones or gallbladder attack' },
            { key: 'weightGain', label: 'Weight gain' },
            { key: 'nausea', label: 'Nausea' },
            { key: 'motionSickness', label: 'Motion sickness' },
            { key: 'gasBloatingHours', label: 'Gas and bloating for hours after eating' },
            { key: 'unexplainedSwelling', label: 'Unexplained swelling in legs and ankles' },
            { key: 'painShoulderBlades', label: 'Pain between shoulder blades' },
            { key: 'headacheOverEyes', label: 'Headache over eyes' },
            { key: 'stoolGrey', label: 'Stool color looks like grey clay' },
            { key: 'hemorrhoids', label: 'Hemorrhoids' },
            { key: 'alcoholAbuse', label: 'Alcohol abuse' },
            { key: 'itchyPeelingFeet', label: 'Itchy and/or peeling feet' },
            { key: 'chronicFatigue', label: 'Chronic fatigue' },
            { key: 'darkUrine', label: 'Dark coloured urine' },
        ],
        checkboxes: {
            title: 'Do you have any of the following conditions?',
            questions: [
                { key: 'hepatitis', label: 'Hepatitis' }, { key: 'cirrhosis', label: 'Cirrhosis' },
                { key: 'wilsonsDisease', label: "Wilson's Disease" }, { key: 'aids', label: 'AIDS' },
                { key: 'gallstones', label: 'Gallstones' }, { key: 'nonalcoholicFattyLiver', label: 'Non-alcoholic Fatty liver disease' },
            ]
        }
    },
    endocrineBloodSugar: {
      title: 'Section 7: Endocrine & Blood Sugar Health',
      notes: true,
      questions: [
        { key: 'craveSweets', label: 'Crave sweets, alcohol or coffee' }, { key: 'difficultyLosingWeight', label: 'Difficulty losing weight' },
        { key: 'needSweetsAfterMeal', label: 'Need sweets after a meal' }, { key: 'waistGirth', label: 'Waist girth is equal to or larger than hip girth' },
        { key: 'increasedThirst', label: 'Increased thirst' }, { key: 'frequentUrination', label: 'Frequent urination' },
        { key: 'relyCoffeeSugar', label: 'Rely on coffee or sugar to get going and stay going' }, { key: 'eatingRelievesFatigue', label: 'Eating relieves fatigue' },
        { key: 'craveSugarAfterEating', label: 'Crave sugar after eating it' }, { key: 'blurredVision', label: 'Blurred vision' },
        { key: 'difficultyMemory', label: 'Difficulty with memory' }, { key: 'feelingFoggy', label: 'Feeling foggy headed' },
        { key: 'feelShaky', label: 'Feel shaky when hungry' }, { key: 'easilyAgitated', label: 'Easily agitated or nervous' },
        { key: 'getLightHeaded', label: 'Get light headed if a meal is late or missed' }, { key: 'getHeadache', label: 'Get a headache if a meal is late or missed' },
        { key: 'suddenWeakness', label: 'Sudden weakness or shakiness' }, { key: 'nightHunger', label: 'Night hunger' },
        { key: 'experienceHungerAfterEating', label: 'Experience hunger after eating' }, { key: 'sleepyAfternoon', label: 'Sleepy in the afternoon' },
        { key: 'bingeUncontrolled', label: 'Binge or uncontrolled eating' },
        { key: 'wakeUpTrouble', label: 'Wake up a few hours after falling asleep & have trouble getting back to sleep' },
      ],
      checkboxes: {
        title: 'Do you have any of the following conditions?',
        questions: [
            {key: 'hypothyroidism', label: 'Hypothyroidism'}, {key: 'hyperthyroidism', label: 'Hyperthyroidism (Graves)'},
            {key: 'addisonsDisease', label: 'Addison\'s Disease'}, {key: 'hypoglycemia', label: 'Hypoglycemia'},
            {key: 'metabolicSyndrome', label: 'Metabolic syndrome'}, {key: 'chronicFatigueSyndrome', label: 'Chronic Fatigue Syndrome'},
            {key: 'type1Diabetes', label: 'Type 1 diabetes'}, {key: 'type2Diabetes', label: 'Type 2 diabetes'},
            {key: 'cushingsSyndrome', label: 'Cushing\'s Syndrome'},
        ]
      }
    },
     neurologicalHealth: {
        title: 'Section 8: Neurological Health',
        notes: true,
        questions: [
            { key: 'concentrationMemory', label: 'Concentration or memory problems' }, { key: 'migraines', label: 'Migraines' },
            { key: 'irritability', label: 'Irritability' }, { key: 'depression', label: 'Depression' },
            { key: 'anxietyPanic', label: 'Anxiety / panic attacks' }, { key: 'worry', label: 'Worry' },
            { key: 'moodSwings', label: 'Mood swings' }, { key: 'vividDreams', label: 'Vivid dreams' },
            { key: 'attentionProblems', label: 'Attention problems' }, { key: 'nightmares', label: 'Nightmares' },
        ],
        checkboxes: {
            title: 'Do you have any of the following conditions?',
            questions: [
                {key: 'addAdhd', label: 'ADD/ADHD'}, {key: 'clinicalDepression', label: 'Clinical depression'},
                {key: 'bipolarDisorder', label: 'Bipolar disorder'}, {key: 'schizophrenia', label: 'Schizophrenia'},
                {key: 'anxietyDisorder', label: 'Anxiety disorder'}, {key: 'traumaticBrainInjury', label: 'Traumatic Brain Injury'},
            ]
        }
    },
    stomachHealth: {
        title: 'Section 9: Stomach Health',
        notes: true,
        questions: [
            { key: 'diarrheaAfterMeals', label: 'Diarrhea after meals' }, { key: 'fingernailsBreak', label: 'Fingernails that break easily' },
            { key: 'strongBodyOdour', label: 'Strong body odour' }, { key: 'undigestedFoodStool', label: 'Undigested food in stool' },
            { key: 'heartburnAcidReflux', label: 'Heartburn or acid reflux' }, { key: 'gasBurpingBloating', label: 'Gas, burping or bloating after meals' },
            { key: 'badBreath', label: 'Bad breath' }, { key: 'feelingHungry', label: 'Feeling hungry after eating a meal' },
            { key: 'feelBetterNotEating', label: 'Feel better when not eating' }, { key: 'feelingOverfull', label: 'Feeling overfull after meals' },
            { key: 'heartburnSpicy', label: 'Heartburn after spicy food, chocolate or caffeine' }, { key: 'digestiveProblemsImprove', label: 'Digestive problems improve after rest' },
            { key: 'antacidsBringRelief', label: 'Antacids bring relief to digestive issues' }, { key: 'stomachPainBurning', label: 'Stomach pain or burning after meals' },
        ]
    },
    digestiveReactions: {
        title: 'Section 10: Digestive Reactions',
        notes: true,
        questions: [
            { key: 'increasedPulse', label: 'Increased pulse after eating' }, { key: 'sinusCongestion', label: 'Sinus congestion' },
            { key: 'alternatingConstipationDiarrhea', label: 'Alternating constipation and diarrhea' }, { key: 'bloatingStarches', label: 'Bloating after eating starches' },
            { key: 'hivesWelts', label: 'Hives or welts after eating' }, { key: 'excessGas', label: 'Excess gas after meals' },
            { key: 'feelingFullHours', label: 'Feeling full for hours after eating' }, { key: 'allergies', label: 'Allergies' },
            { key: 'glutenSensitivity', label: 'Gluten sensitivity' }, { key: 'feelingOverfullMeals', label: 'Feeling overfull after meals' },
            { key: 'cravingsBreadPasta', label: 'Cravings for bread and pasta' }, { key: 'increasingFoodReactions', label: 'Increasing food reactions' },
            { key: 'frequentUrination', label: 'Frequent urination' }, { key: 'feelZonedOut', label: 'Feel zoned out after eating' },
            { key: 'constipationFiber', label: 'Constipation after eating fiber' }, { key: 'achesPainsSwelling', label: 'Aches, pains and swelling' },
            { key: 'yeastInfection', label: 'Yeast infection' }, { key: 'darkCircles', label: 'Dark circles under eyes' },
            { key: 'nailFungus', label: 'Nail fungus' },
        ]
    },
    immuneHealth: {
        title: 'Section 11: Immune Health',
        notes: true,
        questions: [
            { key: 'runnyNose', label: 'Runny nose or nasal drip' }, { key: 'swollenLymph', label: 'Swollen lymph nodes' },
            { key: 'coldFlu', label: 'Cold and flu' }, { key: 'shingles', label: 'Shingles' },
            { key: 'coldSores', label: 'Cold sores' }, { key: 'herpes', label: 'Herpes' },
            { key: 'poorWoundHealing', label: 'Poor wound healing' }, { key: 'dryIrritatedItchyEyes', label: 'Dry/irritated or itchy eyes' },
            { key: 'mucusThroat', label: 'Mucus in throat' }, { key: 'sinusEarInfections', label: 'Sinus or ear infections' },
        ],
        autoimmune: {
            title: 'Do you have any of the following autoimmune conditions?',
            questions: [
                {key: 'lupus', label: 'Lupus'}, {key: 'sjogrens', label: 'Sjogren\'s syndrome'}, {key: 'raynauds', label: 'Raynaud\'s'},
                {key: 'rheumatoidArthritis', label: 'Rheumatoid arthritis'}, {key: 'multipleSclerosis', label: 'Multiple sclerosis'}, {key: 'ibd', label: 'Inflammatory Bowel Disease'},
                {key: 'vasculitis', label: 'Vasculitis'}, {key: 'psoriasis', label: 'Psoriasis'}, {key: 'hashimotos', label: 'Hashimoto\'s'},
            ]
        }
    },
    premenopausalWomen: {
        title: 'Section 12: Pre-Menopausal Women Only',
        notes: true,
        gender: 'female',
        questions: [
            { key: 'perimenopausal', label: 'Perimenopausal' }, { key: 'nightSweats', label: 'Night sweats and/or hot flashes' },
            { key: 'vaginalItchiness', label: 'Vaginal itchiness' }, { key: 'fibrocysticBreasts', label: 'Fibrocystic breasts' },
            { key: 'uterineFibroids', label: 'Uterine fibroids' }, { key: 'facialHairGrowth', label: 'Facial hair growth' },
            { key: 'cycleVaries', label: 'Length of cycle varies each month' }, { key: 'cycleShort', label: 'Cycle is less than 24 days' },
            { key: 'lightBloodFlow', label: 'Light blood flow during cycle' }, { key: 'heavyBloodFlow', label: 'Heavy blood flow during cycle' },
            { key: 'irritableDepressed', label: 'Irritable and/or depressed' }, { key: 'acne', label: 'Acne that does not clear up' },
            { key: 'hairLoss', label: 'Hair loss' }, { key: 'yeastInfections', label: 'Yeast infections' },
            { key: 'breastPainSwelling', label: 'Breast pain and swelling during cycle' }, { key: 'decreasedLibido', label: 'Decreased libido' },
            { key: 'moodSwings', label: 'Mood swings' }, { key: 'lossOfControlUrine', label: 'Loss of control of urine' },
            { key: 'palpitations', label: 'Palpitations' }, { key: 'pms', label: 'PMS' },
        ],
        checkboxes: {
             title: 'Do any of the following apply to you?',
             questions: [
                {key: 'onBirthControl', label: 'On birth control pill'}, {key: 'haveIud', label: 'Have an IUD'}, {key: 'endometriosis', label: 'Endometriosis'},
                {key: 'breastImplants', label: 'Breast implants'}, {key: 'takingHrt', label: 'Taking HRT'}, {key: 'postPartumDepression', label: 'Post partum depression'},
                {key: 'haveBreastCancer', label: 'Have/had breast cancer'}, {key: 'pregnantNursing', label: 'Are pregnant or nursing'}, {key: 'hysterectomy', label: 'Hysterectomy'},
            ]
        }
    },
    menopausalWomen: {
        title: 'Section 13: Menopausal/Post-Menopausal Women Only',
        notes: true,
        gender: 'female',
        questions: [
            { key: 'mentalFogginess', label: 'Mental fogginess' }, { key: 'decreasedInterestSex', label: 'Decreased interest in sex' },
            { key: 'moodSwings', label: 'Mood swings' }, { key: 'depression', label: 'Depression' },
            { key: 'acne', label: 'Acne' }, { key: 'facialHairGrowth', label: 'Facial hair growth' },
            { key: 'vaginalPain', label: 'Vaginal pain, itching, dryness' }, { key: 'shrinkingBreasts', label: 'Shrinking breasts' },
            { key: 'hotFlashes', label: 'Hot flashes' }, { key: 'painfulIntercourse', label: 'Painful intercourse' },
        ],
        additionalFields: [{ key: 'yearsMenopausal', label: 'How many years have you been menopausal?'}]
    },
    menOnly: {
        title: 'Section 14: Men Only',
        notes: true,
        gender: 'male',
        questions: [
            { key: 'uncontrollableSweating', label: 'Uncontrollable sweating' }, { key: 'difficultyMaintainingErection', label: 'Difficulty maintaining erection' },
            { key: 'difficultyConcentrating', label: 'Difficulty concentrating' }, { key: 'muscleSoreness', label: 'Muscle soreness' },
            { key: 'painBurningUrination', label: 'Pain or burning when urinating' }, { key: 'difficultyDribblingUrination', label: 'Difficulty or dribbling when urinating' },
            { key: 'decreasedStamina', label: 'Decreased physical stamina' }, { key: 'decreasedLibido', label: 'Decreased libido' },
            { key: 'incompleteBowelEmptying', label: 'Feeling of incomplete bowel emptying' },
        ],
        additionalFields: [{ key: 'psaTest', label: 'Have you had a prostate-specific antigen (PSA) test done?'}]
    }
};
